import { useEffect, useMemo, useState, useCallback } from "react";

import { validateZip } from "common/utilities";
import { allOperators } from "core/components/QueryBuilder/constants";
import {
  getComplexFieldValue,
  deepMergeObjects,
  areObjectsDeeplyEqual,
  buildComplexFieldState,
  applyUpdateToAuditTrail,
} from "./utilities";
import {
  mockModelOptions,
  stateOptions,
  PRODUCT_BUNDLES,
  FIELD_TYPES,
  DATE_TYPES,
  VALUE_EDITOR_TYPES,
  defaultLicenseState,
} from "./constants";
import { PRODUCTS } from "./products";
import { toast } from "sonner";

const getFieldType = (field) => {
  if (field.values?.length) {
    return FIELD_TYPES.SELECT;
  }

  if (field.id.includes("zip_code")) {
    return FIELD_TYPES.ZIPCODE;
  }

  return FIELD_TYPES[field.type];
};

const createQueryBuilderField = (field) => {
  const type = getFieldType(field);

  const baseProperties = {
    group: field.group,
    inputType: type,
    label: field.name,
    value: field.id,
    name: field.id,
    operators: allOperators,
    original_field: field,
  };

  if (type === FIELD_TYPES.SELECT) {
    return {
      ...baseProperties,
      values: field.values
        // filter out null || undefined (let api team know)
        .filter((value) => {
          // if (!value)
          // console.info("Discovered an empty field value!", { field });
          return !!value;
        })
        .map((value) => {
          return {
            label: value,
            value: value,
            name: value,
          };
        }),
      valueEditorType: VALUE_EDITOR_TYPES.MULTISELECT,
    };
  } else if (type === FIELD_TYPES.DATE) {
    return {
      ...baseProperties,
      // TODO: How to get this?
      dateType: DATE_TYPES.DATE,
      valueEditorType: VALUE_EDITOR_TYPES.DATE,
    };
  } else {
    return baseProperties;
  }
};

const getQueryBuilderFieldsFromCatalog = (table) => {
  const queryBuilderFields = Object.values(table.fields)
    .filter((field) => {
      return field.is_filterable;
    })
    .map(createQueryBuilderField);

  // TODO: Grouping fields for querybuilder has some issues,
  // so just returning ungrouped here
  return queryBuilderFields;
};

const groupFields = (fields) => {
  const groupedFieldsMap = fields.reduce((groupedFields, field) => {
    const fieldOption = {
      label: field.name,
      value: field.id,
    };

    return {
      ...groupedFields,
      [field.category]: groupedFields[field.category]
        ? {
            ...groupedFields[field.category],
            children:
              groupedFields[field.category].children.concat(fieldOption),
          }
        : {
            groupId: field.id,
            label: field.category,
            children: [fieldOption],
          },
    };
  }, {});

  return Object.values(groupedFieldsMap);
};

const getPurchasableAttributeOptionsFromCatalog = (table) => {
  const licensableFieldGroups = Object.values(table.licensable_field_groups);

  return groupFields(licensableFieldGroups);
};

export const getStateOptionsFromCatalog = (table) => {
  const stateAttribute = table?.state_attribute;

  if (stateAttribute) {
    const stateFields = Object.values(table.fields).find((field) => {
      return field.id === stateAttribute;
    });

    if (stateFields.values?.length) {
      return stateFields.values.map((state) => {
        return {
          label: state,
          value: state,
        };
      });
    } else {
      return stateOptions;
    }
  } else {
    return stateOptions;
  }
};

export const getModelOptionsFromCatalog = (models) => {
  const modelKeys = Object.keys(models);

  if (modelKeys.length) {
    return modelKeys.map((modelKey) => {
      const model = models[modelKey];

      return {
        label: model.name,
        value: model.id,
      };
    });
  } else {
    return mockModelOptions;
  }
};

const getOptionsFromCatalog = (table, models, fieldKey) => {
  switch (fieldKey) {
    case "purchasedAttributes":
      return getPurchasableAttributeOptionsFromCatalog(table);

    case "states":
      return getStateOptionsFromCatalog(table);

    case "selectedModels":
      return getModelOptionsFromCatalog(models);

    default:
      return [];
  }
};

const getDerivedDefaultStateFromCatalog = ({
  staticDefault,
  productCatalog = {},
}) => {
  const dynamicDefault = Object.entries(staticDefault).reduce(
    (defaults, [productId, product]) => {
      const catalogEntry = productCatalog[productId] || {};

      const isStatesProductField = product.license?.geography?.states;
      const isStateFilteringOptional =
        catalogEntry.is_state_filtering_required === false;

      if (isStatesProductField && isStateFilteringOptional) {
        const updatedDefaultProduct = {
          ...product,
          license: {
            ...product?.license,
            geography: {
              ...product?.license?.geography,
              states: null,
            },
          },
        };

        defaults[productId] = updatedDefaultProduct;
      } else {
        defaults[productId] = product;
      }

      return defaults;
    },
    {}
  );

  return dynamicDefault;
};

const useProductLicensingFormState = ({
  defaultState,
  serverState,
  productCatalog,
  isLoading,
}) => {
  const [auditTrail, setAuditTrail] = useState([]);

  // UI State for Product Forms
  // Used for dirty checking and displaying configured settings count
  const [formState, setFormState] = useState(() => {
    return Object.entries(PRODUCTS).reduce((defaultFormState, schemaEntry) => {
      const [productId, product] = schemaEntry;
      const totalControls = product.page.settings.reduce(
        (count, setting) =>
          count + setting.controls.filter((control) => control.fieldKey).length,
        0
      );

      return {
        ...defaultFormState,
        [productId]: {
          ...product,
          isDirty: false,
          configuredControls: 0, // dynamically updated in formData
          totalControls: totalControls,
          hasError: false, // dynamically updated in setHandlers
          fieldErrors: Object.keys(product.fields).reduce(
            (defaults, key) => ({ ...defaults, [key]: "" }),
            {}
          ),
        },
      };
    }, {});
  });

  const [serverData, setServerData] = useState(() => {
    return deepMergeObjects(defaultState, serverState);
  });

  const [formData, setFormData] = useState(() => {
    return deepMergeObjects(defaultState, serverState);
  });

  const [isFormReady, setFormReady] = useState(false);

  useEffect(() => {
    if (!isFormReady && !isLoading) {
      setFormReady(true);

      setFormData((previous) => {
        const dynamicDefaultState = getDerivedDefaultStateFromCatalog({
          staticDefault: previous,
          productCatalog,
        });

        return deepMergeObjects(dynamicDefaultState, serverState);
      });
      setServerData((previous) => deepMergeObjects(previous, serverState));
    }
  }, [isLoading, isFormReady, productCatalog, serverState]);

  useEffect(() => {
    if (!isLoading) {
      setFormData((previous) => deepMergeObjects(previous, serverState));
      setServerData((previous) => deepMergeObjects(previous, serverState));
    }
  }, [isLoading, serverState]);

  const getFieldValue = useCallback(
    (productId, fieldId) => {
      const product = PRODUCTS[productId];

      const field = product.fields[fieldId];

      const dataId = field.dataId;

      const fieldValue = getComplexFieldValue({
        dataId: dataId,
        data: formData[productId],
      });

      const defaultControlFormatter = ({ fieldValue }) => fieldValue;
      const fieldControlFormatter =
        field.toControlFormat || defaultControlFormatter;

      const valueInControlFormat = fieldControlFormatter({
        fieldValue: fieldValue,
      });

      return valueInControlFormat;
    },
    [formData]
  );

  const createSetFieldValueHandler = useCallback(
    (productId, fieldId) => {
      return (newValue) => {
        const product = getDynamicProduct(productId);
        const field = product.fields[fieldId];
        const dataId = field.dataId;
        const control = product.page.settings.reduce(
          (foundControl, currentSetting) => {
            const matchingControl = currentSetting.controls.find(
              (control) => control.fieldKey === fieldId
            );

            return matchingControl || foundControl;
          },
          {}
        );

        const defaultValidator = () => {
          return [true, product.fieldErrors];
        };
        const fieldValidator = field.validator || defaultValidator;

        const [isFieldValid, updatedFieldErrors] = fieldValidator({
          product: product,
          field: { fieldId: fieldId, fieldValue: newValue },
        });

        // Used to map control format to API format
        const defaultAPIFormatter = ({ fieldValue }) => fieldValue;
        const fieldAPIFormatter = field.toAPIFormat || defaultAPIFormatter;
        const valueInAPIFormat = fieldAPIFormatter({
          fieldValue: newValue,
          isValid: isFieldValid,
          options: control?.dynamicProps?.catalogFields,
        });

        // Update form data for related product
        setFormData((prevState) => {
          const previousProductState = prevState[productId];

          // !!! May want to rename some things to separate idea of field from data a bit better
          const nextProductState = buildComplexFieldState({
            prevState: previousProductState,
            fieldKey: dataId,
            newValue: valueInAPIFormat,
          });

          // Add update as a partial to audit trail
          setAuditTrail((prevState) =>
            applyUpdateToAuditTrail({
              auditTrail: prevState,
              productId,
              dataId,
              newValue: valueInAPIFormat,
            })
          );

          // Finalize product form updates
          return {
            ...prevState,
            [productId]: nextProductState,
          };
        });

        // Update form state for product
        setFormState((prevState) => {
          const prevProductState = prevState[productId];

          if (prevProductState) {
            return {
              ...prevState,
              [productId]: {
                ...prevProductState,
                hasError: !isFieldValid,
                fieldErrors: updatedFieldErrors,
              },
            };
          } else {
            return prevState;
          }
        });
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData]
  );

  const getCatalogFields = useCallback(
    (productId, dataTableKey, componentType, fieldKey) => {
      // ! Needs to load
      if (!productCatalog) return [];

      switch (componentType) {
        case "queryBuilder":
          if (!productCatalog[productId]) {
            toast.error(`Product ${productId} not found in catalog.`);
            return [];
          }

          if (!productCatalog[productId].data[dataTableKey]) {
            toast.error(
              `Fields for ${productId} not found in ${dataTableKey} table.`
            );
            return [];
          }

          return getQueryBuilderFieldsFromCatalog(
            productCatalog[productId].data[dataTableKey]
          );

        case "mms":
        case "combobox":
          if (fieldKey === "selectedModels") {
            if (!productCatalog[productId]) {
              toast.error(`Product ${productId} not found in catalog.`);
              return [];
            }

            if (!productCatalog[productId].models) {
              toast.error(`Models for ${productId} not found in catalog.`);
              return [];
            }

            return getOptionsFromCatalog(
              null,
              productCatalog[productId].models,
              fieldKey
            );
          }

          if (dataTableKey && !productCatalog[productId]) {
            toast.error(`Product ${productId} not found in catalog.`);
            return [];
          }

          if (dataTableKey && !productCatalog[productId].data[dataTableKey]) {
            toast.error(
              `Fields for ${productId} not found in ${dataTableKey} table.`
            );
            return [];
          }

          return dataTableKey
            ? getOptionsFromCatalog(
                productCatalog[productId].data[dataTableKey],
                null,
                fieldKey
              )
            : fieldKey === "states"
            ? stateOptions
            : [];

        default:
          return [];
      }
    },
    [productCatalog]
  );

  const formSchema = useMemo(() => {
    return Object.values(PRODUCTS).reduce((licensesSchema, product) => {
      const catalogEntry = productCatalog ? productCatalog[product.id] : {};

      const isStateFilteringRequired =
        catalogEntry?.is_state_filtering_required;

      const enhancedProduct = {
        ...product,
        isStateFilteringRequired,
        page: {
          ...product.page,
          isEnabled: {
            ...product.page.isEnabled,
            getValue: () => getFieldValue(product.id, "isEnabled"),
            setValue: createSetFieldValueHandler(product.id, "isEnabled"),
          },
          settings: product.page.settings.map((setting) => {
            return {
              ...setting,
              controls: setting.controls.map((control) => {
                // fieldKey => the UI aliased name for corresponding API property
                const fieldKey = control.fieldKey;
                // dataId => complex dotted id that represents the location of the value withing the API schema
                // Will be null for static components (i.e. descriptions)
                // const dataId = fieldKey
                //   ? product.fields[fieldKey].dataId
                //   : null;

                const convertGrainToDateType = (dateGrain) => {
                  switch (dateGrain) {
                    case "monthly":
                      return "month";

                    case "yearly":
                      return "year";

                    case "quarterly":
                      return "quarter";

                    default:
                      return "date";
                  }
                };

                const dateProps =
                  control.componentType === "date"
                    ? { type: convertGrainToDateType(catalogEntry?.date_grain) }
                    : {};

                return {
                  ...control,
                  dynamicProps: {
                    getValue: () =>
                      fieldKey ? getFieldValue(product.id, fieldKey) : null,
                    setValue: createSetFieldValueHandler(product.id, fieldKey),
                    catalogFields: ["mms", "combobox", "queryBuilder"].includes(
                      control.componentType
                    )
                      ? getCatalogFields(
                          product.id,
                          product.dataTableKey,
                          control.componentType,
                          fieldKey
                        )
                      : null,
                    // TODO: add validators based on field type
                    // !!! There is a different between a control validator and a field validator at the form level
                    validator: fieldKey === "zipcodes" ? validateZip : null,
                    isStateFilteringRequired,

                    ...dateProps,
                  },
                };
              }),
            };
          }),
        },
      };

      licensesSchema[product.id] = enhancedProduct;

      return licensesSchema;
    }, {});
  }, [
    getCatalogFields,
    getFieldValue,
    createSetFieldValueHandler,
    productCatalog,
  ]);

  const getDynamicProduct = useCallback(
    (productId) => {
      if (!formState[productId]) {
        console.log("Missing product from schema", { productId });
      }

      return {
        // meta + ui specific
        ...formState[productId],

        // Product Catalog + UI handlers for Fields
        ...formSchema[productId],

        // License API representation
        data: formData[productId],
      };
    },
    [formState, formSchema, formData]
  );

  const getAllProducts = useCallback(() => {
    return Object.keys(formSchema).map((productId) =>
      getDynamicProduct(productId)
    );
  }, [formSchema, getDynamicProduct]);

  const getProductsEnabled = useCallback(() => {
    return getAllProducts().filter((product) => {
      return product.data.enabled;
    });
  }, [getAllProducts]);

  const getProductIdsEnabled = useCallback(() => {
    return getProductsEnabled().map((product) => product.id);
  }, [getProductsEnabled]);

  const productsEnabled = useMemo(() => {
    return Object.keys(formData)
      .filter((productId) => {
        return formData[productId].enabled;
      })
      .map((productId) => getDynamicProduct(productId))
      .filter((product) => product.meta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const productIdsEnabled = useMemo(() => {
    return productsEnabled.map((product) => product.id);
  }, [productsEnabled]);

  const getProductsFilteredBySegment = useCallback(
    (segmentLicensableProducts = []) => {
      return Object.keys(formSchema)
        .filter((productId) => segmentLicensableProducts.includes(productId))
        .map((productId) => getDynamicProduct(productId));
    },
    [formSchema, getDynamicProduct]
  );

  const clearAuditTrail = useCallback(() => {
    setFormData(serverData);
    setAuditTrail([]);
  }, [serverData]);

  const getPreparedLicenses = useCallback(() => {
    // No updates for sure
    if (!auditTrail.length) {
      return null;
    }

    // Generate and prepare an object containing all UI draft changes
    const mergedUpdatesFromAuditTrail = auditTrail.reduce(
      (mergedUpdates, partialUpdate) => {
        const { productId, productUpdates } = partialUpdate;
        const existingProductUpdates = mergedUpdates[productId] || {};

        mergedUpdates[productId] = deepMergeObjects(
          existingProductUpdates,
          productUpdates,
          "audit trail"
        );

        return mergedUpdates;
      },
      {}
    );

    // Compare those with server state we have

    // Dirty checking and UI state updates
    // 1. Check if new state equals server state
    const areFormUpdatesEqualToServerState = areObjectsDeeplyEqual(
      formData,
      serverData
    );

    // ! Make sure that server state updates are used here and not initially loaded license
    // console.log({ areFormUpdatesEqualToServerState, formData, serverData });

    return areFormUpdatesEqualToServerState
      ? null
      : {
          products: mergedUpdatesFromAuditTrail,
        };
  }, [auditTrail, formData, serverData]);

  const updatedProducts = useMemo(() => {
    const updatedProductKeys =
      getPreparedLicenses()?.products &&
      Object.keys(getPreparedLicenses()?.products);

    return getAllProducts().filter((product) => {
      return updatedProductKeys?.includes(product.id);
    });
  }, [getPreparedLicenses, getAllProducts]);

  const filteredBundles = useMemo(
    () =>
      Object.keys(PRODUCT_BUNDLES).reduce((acc, key) => {
        // Check if the current PRODUCT_BUNDLE id exists in the meta.bundle.id of any item in updatedProducts
        const bundleIdExists = updatedProducts.some(
          (product) => product.meta.bundle.id === PRODUCT_BUNDLES[key].id
        );

        if (bundleIdExists) {
          acc[key] = PRODUCT_BUNDLES[key]; // If exists, add to the accumulator
        }

        return acc;
      }, {}),
    [updatedProducts]
  );

  const getSettingsControlsConfigured = useCallback(() => {
    return Object.keys(formData).reduce((counts, productId) => {
      const productLicense = formData[productId].license || {};

      counts[productId] = Object.entries(productLicense).reduce(
        (count, entry) => {
          const [key, value] = entry;

          if (!value) return count;
          if (!Object.hasOwn(defaultLicenseState[productId].license, key))
            return count;

          if (typeof value === "boolean" && value) return count + 1;
          if (typeof value === "number" && value > 0) return count + 1;
          if (Array.isArray(value) && value.length) return count + 1;
          if (typeof value === "string" && value) return count + 1;
          if (key === "geography") {
            if (value.states?.length) {
              count += 1;
            }
            if (value.zips?.length) {
              count += 1;
            }
          }
          if (key === "dates") {
            if (
              value &&
              value.to &&
              value.from &&
              value.operator === "between"
            ) {
              count += 1;
            }
          }

          return count;
        },
        0
      );

      return counts;
    }, {});
  }, [formData]);

  const importLicense = useCallback((importedConfig = {}) => {
    const importAuditEvents = Object.keys(importedConfig).reduce(
      (productEvents, productId) => {
        const importedLicense = importedConfig[productId];
        const productFields = PRODUCTS[productId]?.fields || {};
        const fieldDataIds = Object.values(productFields)
          ?.map((field) => field.dataId)
          .reduce((collection, dataId) => {
            if (dataId.includes(".")) {
              return collection.concat(dataId.split("."));
            }

            return collection.concat(dataId);
          }, []);

        const generateAuditEvents = (licenseData, allowedFieldKeys = []) => {
          return Object.keys(licenseData).reduce((settingEvents, dataKey) => {
            const data = licenseData[dataKey];

            if (data && typeof data === "object" && !Array.isArray(data)) {
              const strainedData = Object.entries(data).reduce(
                (object, entry) => {
                  const [key, value] = entry;

                  if (allowedFieldKeys.includes(key)) {
                    object[key] = value;
                    return object;
                  }

                  return object;
                },
                {}
              );

              return settingEvents.concat({
                productId: productId,
                productUpdates: { [dataKey]: strainedData },
              });
            }

            return settingEvents.concat({
              productId: productId,
              productUpdates: { [dataKey]: data },
            });
          }, []);
        };

        const generatedAuditEvents = generateAuditEvents(
          importedLicense,
          fieldDataIds
        );

        return productEvents.concat(generatedAuditEvents);
      },
      []
    );

    setAuditTrail(importAuditEvents);

    setFormData((previous) => {
      const mergedConfig = deepMergeObjects(previous, importedConfig);
      return mergedConfig;
    });
  }, []);

  const getChangedEnabledFields = useCallback(() => {
    const productsUpdated = getPreparedLicenses()?.products;
    const result = {};

    for (const key in productsUpdated) {
      const serverEnabled = serverData[key]?.enabled;
      const updateEnabled = productsUpdated[key]?.enabled;

      if (serverEnabled !== updateEnabled) {
        result[key] = productsUpdated[key];
      }
    }

    return result;
  }, [getPreparedLicenses, serverData]);

  return useMemo(
    () => ({
      getDynamicProduct,
      getProductIdsEnabled,
      getProductsEnabled,
      getPreparedLicenses,
      getSettingsControlsConfigured,
      auditTrail,
      clearAuditTrail,
      getAllProducts,
      getProductsFilteredBySegment,
      importLicense,
      updatedProducts,
      filteredBundles,
      getChangedEnabledFields,
      productsEnabled,
      productIdsEnabled,
    }),
    [
      getDynamicProduct,
      getProductIdsEnabled,
      getProductsEnabled,
      getPreparedLicenses,
      getSettingsControlsConfigured,
      auditTrail,
      clearAuditTrail,
      getAllProducts,
      getProductsFilteredBySegment,
      importLicense,
      updatedProducts,
      filteredBundles,
      getChangedEnabledFields,
      productsEnabled,
      productIdsEnabled,
    ]
  );
};

export default useProductLicensingFormState;
